import React, { useRef, useEffect, useCallback } from "react";
import classes from "./NewProductUpdatesCallout.scss";
import classNames from "classnames";
import _ from "lodash";

import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import { ArrowContainer, Popover } from "@toddle-edu/react-tiny-popover";
import { DSProvider, IconButton, Badge, Button } from "@toddle-edu/ds-web";
import { elevations } from "@toddle-edu/ds-theme";
import { CloseOutlined, VideosOutlined } from "@toddle-edu/ds-icons";
import { setNewProductUpdatesCalloutState } from "modules/Services";
import { updateSettings } from "modules/ConfigurationThunks";
import { getSettingValue } from "modules/PermissionModule";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { PRODUCT_UPDATES_CALLOUT_SETTING } from "Constants/permissionConstants";
import { useI18n } from "Hooks";

const ARROW_SIZE = 10;

const STYLES = {
  calloutContainerStyle: {
    zIndex: 2001,
    top: "-4px",
  },
  arrowContainer: {
    padding: 0,
  },
  arrowStyle: {
    top: 1.5,
    borderBottom: `${ARROW_SIZE}px solid var(--surface-primary-enabled)`,
    zIndex: 1,
  },
};

/**
 * Component for displaying new feature updates in a popover callout
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Title of the update
 * @param {string} props.description - Description of the update
 * @param {string} props.videoUrl - Optional URL for the demo video
 * @param {React.ReactElement} props.children - Child elements to wrap (e.g., menu item button)
 * @param {React.ReactElement} props.icon - Optional custom icon component (defaults to VideosOutlined) - used when no children provided
 * @param {string} props.variant - Variant for the IconButton (defaults to "neutral") - used when no children provided
 * @param {string} props.type - Type for the IconButton (defaults to "outlined") - used when no children provided
 * @param {Object} props.newProductUpdatesCalloutState - Redux state for callout visibility
 * @param {string} props.uid - Unique identifier for this specific callout
 * @param {Function} props.setNewProductUpdatesCalloutState - Redux action to update callout state
 * @param {Object} props.newProductUpdatesCalloutPopoverSetting - View count for this callout
 * @param {Function} props.updateSettings - Redux action to update settings
 * @param {boolean} props.showBadge - Whether to show the red dot badge on first view (defaults to true)
 * @param {string} props.calloutButtonVariant - Variant for the callout button (defaults to "neutral")
 * @param {string} props.calloutButtonType - Type for the callout button (defaults to "outlined")
 * @param {boolean} props.persistOnOutsideClick - If true, clicking outside won't mark as viewed and popover will reopen on next visit (defaults to false)
 * @param {boolean} props.considerModalInteractionForViewAction - If true , consider viewed only when interacted with popover modal (defaults to false)
 * @returns {React.ReactElement} New updates callout component
 */
export const NewProductUpdatesCallout = ({
  title,
  description,
  videoUrl,
  children,
  icon = <VideosOutlined />,
  variant = "neutral",
  type = "outlined",
  newProductUpdatesCalloutState,
  uid,
  setNewProductUpdatesCalloutState,
  newProductUpdatesCalloutPopoverSetting,
  updateSettings,
  calloutButtonText = "",
  calloutButtonVariant = "neutral",
  calloutButtonType = "outlined",
  containerStyle,
  customDescriptionClass,
  showPopoverOnMountUntilViewed = false,
  onClickCalloutButton = _.noop,
  showBadge = true,
  considerModalInteractionForViewAction = false,
  iconButtonSize = "small",
  titleStyle = {},
  customCallOutContainerStyle = {},
}) => {
  const { t } = useI18n(["a11y"]);
  const isInitialMount = useRef(true);
  const isSettingsUpdated = useRef(false);
  const closeHandlerInProgress = useRef(false);
  const iconButtonRef = useRef(null);
  const triggerElementRef = useRef(null);
  const containerRef = useRef(null);
  const popoverContentRef = useRef(null);
  const closeButtonRef = useRef(null);

  // Get visibility state from Redux with proper fallback
  const isCalloutOpenedCount =
    newProductUpdatesCalloutPopoverSetting?.[uid]?.viewCount || 0;

  const isCalloutExplicitlyDismissed =
    _.get(newProductUpdatesCalloutState, uid) === false;

  const settingsLoaded = newProductUpdatesCalloutPopoverSetting != null;

  const isCalloutVisible =
    (settingsLoaded && _.get(newProductUpdatesCalloutState, uid, false)) ||
    (!isCalloutExplicitlyDismissed &&
      showPopoverOnMountUntilViewed &&
      isCalloutOpenedCount === 0);

  // Set isInitialMount to false after component mounts to prevent unwanted onClickOutside
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isCalloutVisible) return;

    const timeoutId = setTimeout(() => {
      closeButtonRef.current?.focus?.();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isCalloutVisible]);

  /**
   * Updates the callout visibility state in Redux
   * @param {boolean} state - New visibility state
   */
  const updateCalloutState = useCallback(
    state => {
      setNewProductUpdatesCalloutState({
        ...(newProductUpdatesCalloutState || {}),
        [uid]: state,
      });
    },
    [newProductUpdatesCalloutState, setNewProductUpdatesCalloutState, uid]
  );

  /**
   * Updates view count settings in backend
   */
  const incrementViewCount = useCallback(() => {
    // Only update settings if we haven't already in this session
    if (isSettingsUpdated.current) return;
    try {
      const newValue = {
        ...newProductUpdatesCalloutPopoverSetting,
        [uid]: {
          viewCount:
            (newProductUpdatesCalloutPopoverSetting?.[uid]?.viewCount || 0) + 1,
        },
      };

      updateSettings({
        settings: [
          {
            name: PRODUCT_UPDATES_CALLOUT_SETTING,
            value: JSON.stringify(newValue),
          },
        ],
        showToastMsg: false,
      });

      isSettingsUpdated.current = true;
    } catch (error) {
      console.error("Error updating settings:", error);
    }
  }, [newProductUpdatesCalloutPopoverSetting, uid, updateSettings]);

  /**
   * Handles closing the callout and updates settings if needed
   * @param {Event} e - Click event
   */
  const handleCloseCallout = useCallback(
    (e, options = {}) => {
      const { bypassInitialMountGuard = false } = options;
      // Prevent handling during initial mount phase
      if (isInitialMount.current && !bypassInitialMountGuard) return;

      // Prevent duplicate handling
      if (closeHandlerInProgress.current) return;

      closeHandlerInProgress.current = true;

      if (e) {
        e.stopPropagation();
      }

      // Only update settings on first view when closing
      if (isCalloutOpenedCount === 0) {
        incrementViewCount();
      }

      updateCalloutState(false);
      triggerElementRef.current?.focus?.();
      setTimeout(() => {
        closeHandlerInProgress.current = false;
      }, 100);
    },
    [isCalloutOpenedCount, updateCalloutState, incrementViewCount]
  );

  /**
   * Handles toggling the callout visibility
   * @param {Event} e - Click event
   */
  const handleToggleCallout = useCallback(
    e => {
      // Prevent default behavior and propagation
      if (e) {
        triggerElementRef.current =
          e.currentTarget || triggerElementRef.current;
        e.preventDefault();
        e.stopPropagation();

        // Prevent event bubbling to avoid triggering onClickOutside
        if (e.nativeEvent) {
          e.nativeEvent.stopImmediatePropagation();
        }
      }

      // Prevent toggle if we're in the middle of closing
      if (closeHandlerInProgress.current) {
        return;
      }

      // Toggle visibility state
      const newState = !isCalloutVisible;
      updateCalloutState(newState);

      // If we're closing the popover and it's the first view, update settings
      if (
        !newState &&
        isCalloutOpenedCount === 0 &&
        !considerModalInteractionForViewAction
      ) {
        incrementViewCount();
      }
    },
    [
      isCalloutVisible,
      isCalloutOpenedCount,
      updateCalloutState,
      incrementViewCount,
      considerModalInteractionForViewAction,
    ]
  );

  const handleVideoContainerClick = useCallback(e => {
    e.stopPropagation();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }
  }, []);

  const handleContentButtonClick = useCallback(
    e => {
      e.stopPropagation();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }
      onClickCalloutButton(e);
      handleCloseCallout(e);
    },
    [onClickCalloutButton, handleCloseCallout]
  );

  const handlePopoverContentKeyDown = useCallback(
    e => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (considerModalInteractionForViewAction) {
          handleToggleCallout(e);
        } else {
          handleCloseCallout(e, { bypassInitialMountGuard: true });
        }
        return;
      }

      if (e.key !== "Tab" || !popoverContentRef.current) return;

      const focusableElements = Array.from(
        popoverContentRef.current.querySelectorAll(
          'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute("disabled"));

      if (_.isEmpty(focusableElements)) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      } else if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    },
    [
      considerModalInteractionForViewAction,
      handleCloseCallout,
      handleToggleCallout,
    ]
  );

  const renderCalloutContent = useCallback(
    ({ position, childRect, popoverRect }) => (
      <ArrowContainer
        position={position}
        childRect={childRect}
        popoverRect={popoverRect}
        arrowSize={ARROW_SIZE}
        arrowColor="var(--border-secondary)"
      >
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowSize={ARROW_SIZE}
          style={STYLES.arrowContainer}
          arrowStyle={STYLES.arrowStyle}
        >
          <div
            ref={popoverContentRef}
            className={classes.demoVideoContentContainer}
            style={{ ...elevations["elevation-2B"], ...containerStyle }}
            role="dialog"
            aria-label={title}
            onKeyDown={handlePopoverContentKeyDown}
          >
            <div
              className={classNames("text-heading-6", classes.headingContainer)}
              style={{ ...titleStyle }}
            >
              <h6
                style={{ display: "inline" }}
                className={classNames(classes.contentHeading)}
              >
                {title}
              </h6>
              <IconButton
                ref={closeButtonRef}
                icon={<CloseOutlined />}
                dsVersion="2.0"
                size={iconButtonSize}
                variant="neutral"
                type="plain"
                onClick={
                  considerModalInteractionForViewAction
                    ? handleToggleCallout
                    : e =>
                        handleCloseCallout(e, { bypassInitialMountGuard: true })
                }
              />
            </div>
            <div className={classes.demoVideoContentBody}>
              <span
                className={classNames("text-body-l", customDescriptionClass)}
              >
                {description}
              </span>
              {videoUrl ? (
                <DSProvider dsVersion="1.0">
                  <div
                    className={classes.videoPlayerContainer}
                    onClick={handleVideoContainerClick}
                  >
                    <iframe
                      src={videoUrl}
                      width="100%"
                      height="100%"
                      title={
                        title
                          ? t("a11y:demo_video_with_title", { title })
                          : t("a11y:demo_video")
                      }
                      allowFullScreen="true"
                    />
                  </div>
                </DSProvider>
              ) : null}

              {calloutButtonText ? (
                <Button
                  dsVersion="2.0"
                  variant={calloutButtonVariant}
                  type={calloutButtonType}
                  isFullWidth={true}
                  onClick={handleContentButtonClick}
                >
                  {calloutButtonText}
                </Button>
              ) : null}
            </div>
          </div>
        </ArrowContainer>
      </ArrowContainer>
    ),
    [
      title,
      description,
      videoUrl,
      handleCloseCallout,
      handleVideoContainerClick,
      calloutButtonText,
      calloutButtonVariant,
      calloutButtonType,
      containerStyle,
      customDescriptionClass,
      handleContentButtonClick,
      handlePopoverContentKeyDown,
    ]
  );

  const handleIconButtonClick = useCallback(
    e => {
      handleToggleCallout(e);
    },
    [handleToggleCallout]
  );

  /**
   * Handles click on child element (wrapped menu item)
   * Closes popover when child is clicked
   */
  const handleChildElementClick = useCallback(
    e => {
      // Close the popover when child is clicked (menu item navigation)
      if (isCalloutVisible) {
        handleToggleCallout(e);
      }
    },
    [isCalloutVisible, handleToggleCallout]
  );

  /**
   * Clones children and adds click handler to close popover
   */
  const wrappedChildren = children
    ? React.cloneElement(React.Children.only(children), {
        onClick: e => {
          // Call original onClick if it exists
          if (children.props.onClick) {
            children.props.onClick(e);
          }
          handleChildElementClick(e);
        },
      })
    : null;

  const badgeAndButton = children ? (
    <div ref={containerRef} className={classes.calloutWrapper}>
      <ConditionalWrapper
        condition={showBadge && isCalloutOpenedCount === 0}
        type="wrapperAsElement"
        wrapper={
          <Badge
            dsVersion="2.0"
            type="dot"
            variant="active"
            shouldAnimateActiveDotBadge={true}
          />
        }
      >
        {wrappedChildren}
      </ConditionalWrapper>
    </div>
  ) : (
    <div ref={containerRef}>
      <ConditionalWrapper
        condition={showBadge && isCalloutOpenedCount === 0}
        type="wrapperAsElement"
        wrapper={
          <Badge
            dsVersion="2.0"
            type="dot"
            variant="active"
            shouldAnimateActiveDotBadge={true}
          />
        }
      >
        <IconButton
          ref={iconButtonRef}
          variant={variant}
          type={type}
          dsVersion="2.0"
          icon={icon}
          onClick={handleIconButtonClick}
        />
      </ConditionalWrapper>
    </div>
  );

  return isCalloutVisible ? (
    <Popover
      isOpen={isCalloutVisible}
      positions={["bottom"]}
      align="end"
      padding={12}
      boundaryInset={20}
      reposition={true}
      containerClassName={classes.popoverContainer}
      containerStyle={{
        ...STYLES.calloutContainerStyle,
        ...customCallOutContainerStyle,
      }}
      onClickOutside={
        considerModalInteractionForViewAction
          ? handleToggleCallout
          : handleCloseCallout
      }
      content={renderCalloutContent}
    >
      {badgeAndButton}
    </Popover>
  ) : (
    badgeAndButton
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();
  const newProductUpdatesCalloutPopoverSetting = getSettingValue({
    name: PRODUCT_UPDATES_CALLOUT_SETTING,
    userId: userInfo?.id,
    readFromUserAndOrgLevel: true,
  });
  return {
    newProductUpdatesCalloutState: _.get(
      state,
      "app_services.newProductUpdatesCalloutState"
    ),
    newProductUpdatesCalloutPopoverSetting,
  };
};

const mapActionCreators = {
  setNewProductUpdatesCalloutState,
  updateSettings,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  NewProductUpdatesCallout
);
